import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

export default function AdminPage() {
  const [bookings, setBookings] = useState([]);
  const [newsletters, setNewsletters] = useState([]);
  const [careers, setCareers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [bookingsRes, newslettersRes, careersRes, contactsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/bookings`),
        fetch(`${BACKEND_URL}/api/newsletter`),
        fetch(`${BACKEND_URL}/api/careers`),
        fetch(`${BACKEND_URL}/api/contacts`)
      ]);

      setBookings(await bookingsRes.json());
      setNewsletters(await newslettersRes.json());
      setCareers(await careersRes.json());
      setContacts(await contactsRes.json());
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-24 min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-xl text-gray-600">Loading submissions...</p>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-20 min-h-screen bg-gray-50">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
        <p className="text-gray-600 mb-8">View all form submissions from your website</p>

        <Tabs defaultValue="bookings" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="bookings">
              Bookings ({bookings.length})
            </TabsTrigger>
            <TabsTrigger value="careers">
              Careers ({careers.length})
            </TabsTrigger>
            <TabsTrigger value="contacts">
              Contacts ({contacts.length})
            </TabsTrigger>
            <TabsTrigger value="newsletter">
              Newsletter ({newsletters.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="space-y-4 mt-6">
            {bookings.length === 0 ? (
              <Card className="p-8 text-center text-gray-500">
                No booking requests yet
              </Card>
            ) : (
              bookings.map((booking) => (
                <Card key={booking.id} className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">
                        {booking.contactInfo.fullName}
                      </h3>
                      <p className="text-gray-600">
                        {booking.contactInfo.phone} • {booking.contactInfo.email}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-[#0B7CB5]">
                        ${booking.totalPrice}
                      </p>
                      <Badge variant="secondary">{booking.status}</Badge>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Square Footage: {booking.formData.squareFootage}</p>
                      <p className="text-gray-600">Bedrooms: {booking.formData.bedrooms}</p>
                      <p className="text-gray-600">Bathrooms: {booking.formData.bathrooms}</p>
                      <p className="text-gray-600">Kitchen: {booking.formData.kitchen ? 'Yes' : 'No'}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Extra Rooms: {booking.formData.extraRooms}</p>
                      <p className="text-gray-600">Pets: {booking.formData.pets}</p>
                      <p className="text-gray-600">Stairs: {booking.formData.stairs}</p>
                      <p className="text-gray-600 text-xs mt-2">
                        {new Date(booking.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="careers" className="space-y-4 mt-6">
            {careers.length === 0 ? (
              <Card className="p-8 text-center text-gray-500">
                No career applications yet
              </Card>
            ) : (
              careers.map((app) => (
                <Card key={app.id} className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{app.fullName}</h3>
                      <p className="text-gray-600">{app.phone} • {app.email}</p>
                      <p className="text-gray-600 text-sm">
                        {app.address}, {app.city}, {app.state} {app.zipCode}
                      </p>
                    </div>
                    <Badge variant={app.status === 'new' ? 'default' : 'secondary'}>
                      {app.status}
                    </Badge>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div>
                      <p className="font-semibold text-gray-900">Experience:</p>
                      <p className="text-gray-700">{app.experience}</p>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Availability:</p>
                      <p className="text-gray-700">{app.availability}</p>
                    </div>
                    {app.reference1Name && (
                      <div>
                        <p className="font-semibold text-gray-900">References:</p>
                        <p className="text-gray-700">
                          {app.reference1Name} - {app.reference1Phone}
                          {app.reference2Name && ` • ${app.reference2Name} - ${app.reference2Phone}`}
                        </p>
                      </div>
                    )}
                    <p className="text-gray-600 text-xs pt-2">
                      {new Date(app.timestamp).toLocaleString()}
                    </p>
                  </div>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="contacts" className="space-y-4 mt-6">
            {contacts.length === 0 ? (
              <Card className="p-8 text-center text-gray-500">
                No contact submissions yet
              </Card>
            ) : (
              contacts.map((contact) => (
                <Card key={contact.id} className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{contact.fullName}</h3>
                      <p className="text-gray-600">{contact.phone} • {contact.email}</p>
                    </div>
                    <Badge variant="secondary">{contact.status}</Badge>
                  </div>
                  {contact.notes && (
                    <div className="mb-4">
                      <p className="font-semibold text-gray-900 text-sm">Message:</p>
                      <p className="text-gray-700">{contact.notes}</p>
                    </div>
                  )}
                  <p className="text-gray-600 text-xs">
                    {new Date(contact.timestamp).toLocaleString()}
                  </p>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="newsletter" className="space-y-4 mt-6">
            {newsletters.length === 0 ? (
              <Card className="p-8 text-center text-gray-500">
                No newsletter subscriptions yet
              </Card>
            ) : (
              <Card className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  Newsletter Subscribers
                </h3>
                <div className="space-y-2">
                  {newsletters.map((sub) => (
                    <div key={sub.id} className="flex justify-between items-center py-2 border-b">
                      <span className="text-gray-900">{sub.email}</span>
                      <span className="text-xs text-gray-600">
                        {new Date(sub.timestamp).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
